import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Assignment, Member, Workflow } from "../api/types";

// --- roster (admin) ---------------------------------------------------------

export function useMembers() {
  return useQuery({
    queryKey: ["team", "members"],
    queryFn: () => api<{ members: Member[]; clerk_lookup: boolean }>("/api/team/members"),
    staleTime: 30_000,
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; name?: string; roles?: string[]; default_rate_pennies?: number | null }) =>
      api<Member>("/api/team/members", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "members"] }),
  });
}

/** Grant or revoke the signed-in admin's OWN proofer role — "I'll also proof".
 *  Touches both `me` (the shell reads roles/entitlements from it) and the roster
 *  (the self-row appears / disappears). */
export function useSelfProofer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) =>
      api<{ roles: string[]; proofer: boolean }>("/api/me/proofer", { method: on ? "POST" : "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["team", "members"] });
    },
  });
}

/** Set the caller's OWN display name (empty string clears it → back to the Clerk
 *  name). Refreshes `me` (drives the shell) and the roster (a self-Member's name
 *  is kept in sync server-side). */
export function useSetMyName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ name: string }>("/api/me/display-name", { method: "PUT", body: JSON.stringify({ name }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["team", "members"] });
    },
  });
}

export function usePatchMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Pick<Member, "name" | "status">> & { roles?: string[]; default_rate_pennies?: number | null; unavailable?: string[] } }) =>
      api<Member>(`/api/team/members/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "members"] }),
  });
}

export function useResendInvite() {
  return useMutation({
    mutationFn: (id: string) => api(`/api/team/members/${id}/resend`, { method: "POST" }),
  });
}

export interface ClerkCheck {
  publishable_env: string;
  secret_env: string;
  instances_match: boolean;
  issuer: string | null;
  backend_api: { ok: boolean; status: number | null; detail: string | null };
  email_checked: string | null;
  clerk_user_found: boolean | null;
}

/** Ask the server whether Clerk actually works from here (and, optionally,
 *  whether a given proofer has a Clerk account). */
export function useClerkCheck() {
  return useMutation({
    mutationFn: (email?: string) =>
      api<ClerkCheck>(`/api/team/clerk-check${email ? `?email=${encodeURIComponent(email)}` : ""}`),
  });
}

export function useClaimLink() {
  return useMutation({
    mutationFn: (id: string) =>
      api<{ claim_url: string }>(`/api/team/members/${id}/claim-link`, { method: "POST" }),
  });
}

/** The proofer binds their own account with the single-use link — no Clerk
 *  lookup, no `email` claim needed. */
export function useClaimMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api<{ ok: boolean; name: string }>(`/api/me/claim/${token}`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/team/members/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "members"] }),
  });
}

// --- assignments ------------------------------------------------------------

/** A proofer's own log (My Work). */
export function useMyAssignments() {
  return useQuery({
    queryKey: ["me", "assignments"],
    queryFn: () => api<{ assignments: Assignment[]; proofer_name: string }>("/api/me/assignments"),
    refetchInterval: 20_000,
  });
}

/** Admin view of one proofer's log — what that proofer sees. */
export function useMemberAssignments(memberId: string | undefined) {
  return useQuery({
    queryKey: ["team", "members", memberId, "assignments"],
    enabled: !!memberId,
    queryFn: () =>
      api<{ assignments: Assignment[]; proofer_name: string; member: Member }>(
        `/api/team/members/${memberId}/assignments`,
      ),
  });
}

/** Assignments on a project (admin — used by the board's Tracking View). */
export function useProjectAssignments(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "assignments"],
    enabled: !!projectId,
    queryFn: () =>
      api<{ assignments: Assignment[] }>(`/api/projects/${projectId}/assignments`).then((r) => r.assignments),
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body: { member_id: string; scope: "all" | "subset"; scope_files?: string[]; service?: string | null } }) =>
      api<Assignment>(`/api/projects/${projectId}/assignments`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "assignments"] });
      qc.invalidateQueries({ queryKey: ["team"] });
      qc.invalidateQueries({ queryKey: ["workflows"] });  // refresh the board's Proofer cell
    },
  });
}

export function usePatchAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api<Assignment>(`/api/assignments/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    // Handing an assignment to someone else changes the BOARD's view of who's
    // on a title — but `assigned_proofers` is derived server-side, so without
    // this the Timeline's row sits still and then snaps into its new card a
    // round-trip later. The new name comes from the roster cache, which the
    // Timeline has already loaded.
    onMutate: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const to = patch.member_id;
      if (typeof to !== "string") return;
      const member = qc.getQueryData<{ members: Member[] }>(["team", "members"])
        ?.members.find((m) => m.id === to);
      if (!member) return;
      qc.setQueryData<Workflow[]>(["workflows"], (cur) => cur?.map((w) =>
        (w.assigned_proofers || []).some((a) => a.assignment_id === id)
          ? {
            ...w,
            assigned_proofers: w.assigned_proofers.map((a) =>
              a.assignment_id === id
                ? { ...a, member_id: member.id, name: member.name || member.email }
                : a),
          }
          : w));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["workflows"] });  // refresh the board's Proofer cell
    },
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/assignments/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["team"] });
      qc.invalidateQueries({ queryKey: ["workflows"] });  // refresh the board's Proofer cell
    },
  });
}

/** A proofer moves their own row: To do → In progress → Complete. */
export function useSetMyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api<Assignment>(`/api/assignments/${id}/status`, {
        method: "POST", body: JSON.stringify({ status }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "assignments"] }),
  });
}

// --- Timeline capacity ------------------------------------------------------

/** Audio-hours a proofer gets through in a day — the yardstick the Timeline's
 *  workload strips are read against. Per account; the server supplies the
 *  default so there's one source of truth for it rather than two. */
export function useTimelineNorm() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["me", "timeline-norm"],
    queryFn: () => api<{ norm_h: number }>("/api/me/timeline-norm").then((r) => r.norm_h),
    staleTime: 300_000,
  });
  const save = useMutation({
    mutationFn: (norm_h: number) =>
      api<{ norm_h: number }>("/api/me/timeline-norm", { method: "PUT", body: JSON.stringify({ norm_h }) }),
    // Optimistic: the strips rescale as you type rather than after a round-trip.
    onMutate: (norm_h: number) => { qc.setQueryData(["me", "timeline-norm"], norm_h); },
    onSettled: () => qc.invalidateQueries({ queryKey: ["me", "timeline-norm"] }),
  });
  return { norm: q.data, setNorm: (n: number) => save.mutate(n) };
}

/** Toggle one day of a proofer's availability. The roster row is the source of
 *  truth, so this reuses the member PATCH rather than inventing a second store. */
export function useToggleAvailability() {
  const qc = useQueryClient();
  const nextFor = (m: Member, day: string) => {
    const cur = m.unavailable || [];
    return cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day].sort();
  };
  return useMutation({
    mutationFn: ({ member, day }: { member: Member; day: string }) =>
      api<Member>(`/api/team/members/${member.id}`, {
        method: "PATCH", body: JSON.stringify({ unavailable: nextFor(member, day) }),
      }),
    onMutate: ({ member, day }: { member: Member; day: string }) => {
      const next = nextFor(member, day);
      qc.setQueryData<{ members: Member[]; clerk_lookup: boolean }>(["team", "members"], (old) =>
        old ? { ...old, members: old.members.map((m) => (m.id === member.id ? { ...m, unavailable: next } : m)) } : old);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["team", "members"] }),
  });
}
